/**
 * Layout: standard — the grid card. Every slot, stacked vertically.
 *
 * DUAL CLASS CONTRACT, deliberate and shared by all four layouts:
 *   - core's names (.item-card, .card-img-container, .card-img, .card-body,
 *     .product-title, .product-category, .product-price, .like-action,
 *     .cart-indicator) keep webshop_cart.scss + webhq_overrides.css applying
 *     and keep bind_add_to_cart_action()/bind_wishlist_action() finding their
 *     targets. This is what makes the cutover a parity change, not a redesign.
 *   - .rfhq-pc-* names are added alongside as styling hooks.
 *
 * The pieces live in _shared.js; a layout is a composition of them.
 */

import { el } from '../../dom.js';
import { renderAction } from '../action/index.js';
import {
	renderMedia,
	renderTitleRow,
	renderPrice,
	renderVariantPickerRow,
	renderSizes,
	renderStock,
	renderConsumerDiscount,
	renderOffers,
	renderCategory,
	renderRating,
	renderActionsSlot,
	renderWishlist,
} from './_shared.js';
import { cardRoot } from './root.js';

export function render(model, ctx) {
	// Wishlist rendered into the media box below (a floating glass toolbar
	// over the photo) instead of renderTitleRow's own inline copy -- suppress
	// it there via a shadowed settings flag rather than editing the shared
	// function, so compact/minimal/featured (which still want it inline)
	// are untouched. cart-indicator's own flag is passed through as-is.
	const titleRowCtx = { ...ctx, settings: { ...ctx.settings, enable_wishlist: false } };

	const body = el('div', { class: 'card-body text-left card-body-flex rfhq-pc-body', style: 'width:100%' }, [
		// Above the title — the position rfhq_variant_swatch_layout.js used to
		// relocate webhq's injected rows to. Rendering there natively makes that
		// script a no-op on these cards rather than a competing writer.
		renderVariantPickerRow(model, ctx),
		renderTitleRow(model, titleRowCtx),
		// renderEyebrow (a standalone "HELIX" brand badge) deliberately dropped
		// here: raftor_webhq's own patch already injects a combined
		// brand+category line (.product-meta-row, e.g. "HELIX • Men") above the
		// title on /all-products, so this repeated the brand name a second time
		// right below it for no reason. renderCategory (websitehq's own,
		// currently unpopulated for this data) is left in case a future model
		// carries a distinct category value webhq's line doesn't already cover.
		renderCategory(model),
		renderRating(model),
		renderSizes(model, ctx),
		renderPrice(model, ctx),
		renderConsumerDiscount(model),
		renderStock(model),
		renderOffers(model),
		renderActionsSlot(model, ctx, renderAction),
	]);

	const media = renderMedia(model);
	// Floating glass toolbar over the photo: a real child of the media box
	// (not a CSS trick reaching across from card-body) so it can share
	// .card-img-container's own position:relative context cleanly and stay
	// vertically anchored to the actual image regardless of how tall the
	// text block below ends up being.
	const toolbar = el('div', { class: 'rfhq-pc-media-toolbar' }, []);
	media.appendChild(toolbar);

	// Quick-view (the eye icon): renderMedia already built and appended it
	// as a plain child of the media box (its own top-left corner spot,
	// still styled by .rfhq-pc-quickview elsewhere) -- relocate that exact
	// node into the toolbar via appendChild (moves, not clones, so its
	// dataset/click-binding survive untouched) rather than rendering a
	// second copy.
	const quickview = media.querySelector('.rfhq-pc-quickview');
	if (quickview) toolbar.appendChild(quickview);

	if (ctx.settings.enable_wishlist && model.actions.kind !== 'explore' && model.itemCode) {
		toolbar.appendChild(renderWishlist(model));
	}

	const cardEl = cardRoot(model, ctx, 'standard', [media, body]);

	// Trial-room toggle: cardRoot (root.js) already builds and places it as
	// a sibling of media/body under .card, gated on
	// window.rfhqTrialRoomEnabled -- same relocate-don't-rebuild approach as
	// quickview above. Only exists in the DOM at all when that flag is on,
	// so this is a no-op otherwise.
	const trialToggle = cardEl.querySelector('.rfhq-pc-trialroom-toggle');
	if (trialToggle) toolbar.appendChild(trialToggle);

	return cardEl;
}
